import React, { FC } from 'react'
import { useToggle } from 'react-use'
import { useSpBreakPoint } from '../../hooks/use_sp_breakpoint'
import FilterButton from '../../components/buttons/filter_button'
import CommonTemplate from '../../components/templates/common_template'
import Select from '../../components/select/select'
import useCodes from '../../hooks/use_codes'
import Head2 from '../../components/heads/head2'
import PagiNationCursor from '../../components/pagination/pagination_cursor'
import useUserRanking from './hooks/use_user_ranking'
import UserCard from '../../components/card/user_card'
import UserDetailCard from './components/card/user_detail_card'
import { RankingPeriod, RankingUserStat } from '../../clients/api_client'
import { RankingPeriodCode, RankingUserStatCode } from '../../model/Code'
import LoadingSpinner from '../../components/loading/loading_spinner'
import RankingTab from './components/tab/ranking_tab'

const UserRankingPage: FC = () => {
  const PAGE_SIZE = 20

  const isSp = useSpBreakPoint()
  const [codes] = useCodes()
  const [
    ranking,
    currentPeriod,
    currentStat,
    currentGroup,
    currentGroupValue,
    size,
    currentCursor,
    rankingLoading,
    rankingHandler,
  ] = useUserRanking({
    period: RankingPeriod.All,
    stat: RankingUserStat.SendPointCount,
    getLimit: PAGE_SIZE,
  })
  const [formShow, toggle] = useToggle(true)

  const IndexSectionRef = React.createRef<HTMLDivElement>()
  const scrollToIndexSection = () => {
    if (IndexSectionRef.current) {
      const elementPosition =
        IndexSectionRef.current.getBoundingClientRect().top + window.scrollY
      const offsetPosition = elementPosition - 100
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  const head = (
    <div className="mb-4">
      <Head2
        title={`ユーザーランキング`}
        subTitle={
          ranking.length > 0
            ? `全件${String(size)}中 ${currentCursor + 1}~${
                currentCursor + ranking.length
              }件`
            : ''
        }
      />
    </div>
  )

  const filterPeriodButtons = (
    <div className="flex justify-start flex-wrap">
      {codes.rankingPeriodCodes.map((period: RankingPeriodCode) => {
        return (
          <div key={`ranking-period-${period.code}`} className="pr-2 pb-2">
            <FilterButton
              label={period.label}
              value={period.code}
              isActive={period.code === currentPeriod.toString()}
              onClick={() =>
                rankingHandler.search(
                  period.code,
                  currentStat,
                  currentGroup,
                  currentGroupValue
                )
              }
            />
          </div>
        )
      })}
    </div>
  )

  const filterStatButtons = (
    <div className="flex justify-start flex-wrap">
      {codes.rankingUserStatCodes.map((stat: RankingUserStatCode) => {
        return (
          <div key={`ranking-stat-${stat.code}`} className="pr-2 pb-2">
            <FilterButton
              label={stat.label}
              value={stat.code}
              isActive={stat.code === currentStat.toString()}
              onClick={() =>
                rankingHandler.search(
                  currentPeriod,
                  stat.code,
                  currentGroup,
                  currentGroupValue
                )
              }
            />
          </div>
        )
      })}
    </div>
  )

  const filterGroupButtons = (
    <div className="flex justify-start flex-wrap">
      {/* リリースに伴い一旦非表示 */}
      {/* <div className="pr-2 pb-2">
        <Select
          id="occupation"
          placeholder="業種カテゴリー"
          currentValue={currentGroupValue ?? ''}
          currentGroup={currentGroup ?? ''}
          onSelect={(e: React.ChangeEvent<HTMLSelectElement>) =>
            rankingHandler.search(
              currentPeriod,
              currentStat,
              'occupation',
              e.currentTarget.value
            )
          }
          items={codes.occupationCodes.map((item) => {
            return {
              value: item.code,
              label: item.value,
            }
          })}
          brankLabel="全て"
        />
      </div> */}
      <div className="pr-2 pb-2">
        <Select
          id="prefecture"
          placeholder="都道府県別"
          currentValue={currentGroupValue ?? ''}
          currentGroup={currentGroup ?? ''}
          onSelect={(e: React.ChangeEvent<HTMLSelectElement>) =>
            rankingHandler.search(
              currentPeriod,
              currentStat,
              'prefecture',
              e.currentTarget.value
            )
          }
          items={codes.prefectures.map((prefecture) => {
            return {
              value: prefecture,
              label: prefecture,
            }
          })}
          brankLabel="全て"
        />
      </div>
      <div className="pr-2 pb-2 max-w-full">
        <Select
          id="sdgs"
          placeholder="SDGsカテゴリー"
          currentValue={currentGroupValue ?? ''}
          currentGroup={currentGroup ?? ''}
          onSelect={(e: React.ChangeEvent<HTMLSelectElement>) =>
            rankingHandler.search(
              currentPeriod,
              currentStat,
              'sdgs',
              e.currentTarget.value
            )
          }
          items={codes.sdgsCategories.map((item) => {
            return {
              value: item.code,
              label: `${item.code}.${item.value}`,
            }
          })}
          brankLabel="全て"
        />
      </div>
    </div>
  )

  const seachResultTopElem = (
    <div className="w-full">
      {ranking
        .filter((item) => item.rank <= 5)
        .map((item, index) => {
          return (
            <div key={`ranking-${String(index)}`} className="pb-4">
              <UserDetailCard
                user={item.user}
                rank={item.rank}
                onFollow={rankingHandler.follow}
              />
            </div>
          )
        })}
    </div>
  )

  const seachResultElem = (
    <div className="flex justify-between flex-wrap sm:p-0 p-4">
      {ranking
        .filter((item) => item.rank > 5)
        .map((item, index) => {
          return (
            <>
              {/* lgサイズ以上は表示を変更する */}
              <div
                className="py-4 w-[calc(20%-1rem)] hidden lg:block"
                key={`ranking-${String(index)}`}
              >
                <UserCard
                  showRank
                  flex={false}
                  rank={item.rank}
                  user={item.user}
                  imgSize="w-[120px] h-[120px]"
                  className="!w-full"
                />
              </div>
              {/* lgサイズ以下はseachResultTopElemと同じ表示 */}
              <div
                key={`ranking-${String(index)}`}
                className="pb-4 w-full lg:hidden"
              >
                <UserDetailCard
                  user={item.user}
                  rank={item.rank}
                  onFollow={rankingHandler.follow}
                />
              </div>
            </>
          )
        })}
      {ranking.length === 0 && (
        <div className="w-full text-center">該当するユーザーがいません</div>
      )}
    </div>
  )

  const paginationCursor =
    ranking.length > 0 ? (
      <PagiNationCursor
        pageCount={Math.ceil(size / PAGE_SIZE)}
        numRange={3}
        activePageNum={Math.floor(currentCursor / PAGE_SIZE) + 1}
        next={() => {
          scrollToIndexSection()
          rankingHandler.search(
            currentPeriod,
            currentStat,
            currentGroup,
            currentGroupValue,
            currentCursor + PAGE_SIZE
          )
        }}
        prev={() => {
          scrollToIndexSection()
          rankingHandler.search(
            currentPeriod,
            currentStat,
            currentGroup,
            currentGroupValue,
            currentCursor - PAGE_SIZE
          )
        }}
        jump={(pageNum: number) => {
          scrollToIndexSection()
          rankingHandler.search(
            currentPeriod,
            currentStat,
            currentGroup,
            currentGroupValue,
            (pageNum - 1) * PAGE_SIZE
          )
        }}
      />
    ) : null

  const rankigTab = (
    <RankingTab
      activeIndex={2}
      isSp={isSp}
      isFormShow={formShow}
      toggleContent={toggle}
    >
      <p className="font-bold mb-1">ランキングの種類</p>
      <div className="mb-2">
        <div className="pb-1">{filterPeriodButtons}</div>
        <div className="pb-1">{filterStatButtons}</div>
      </div>
      <p className="font-bold mb-1">カテゴリーで絞る</p>
      <div>{filterGroupButtons}</div>
    </RankingTab>
  )

  const content = (
    <CommonTemplate isPadding={!isSp} isSp={isSp} additonalTopNode>
      <div className="pb-4">
        {rankigTab}
        <div className="px-4 sm:px-0">
          <div className="pt-12">{head}</div>
        </div>
        {rankingLoading ? (
          <LoadingSpinner className="mx-auto" />
        ) : (
          <>
            <div ref={IndexSectionRef} />
            <div>{seachResultTopElem}</div>
            <div className="pb-8">{seachResultElem}</div>
            <div className="pb-8">{paginationCursor}</div>
          </>
        )}
      </div>
    </CommonTemplate>
  )

  return content
}

export default UserRankingPage
